import { h } from 'preact'

import type { QaColor } from './types'
import type { QaStrings } from './i18n'
import { QA_COLOR_LABEL_KEY, QA_COLOR_MODIFIER } from './ui'

/**
 * The heartbeat/ECG pastille — purely presentational. It renders the page color
 * as a semaphore modifier and emits hover/open callbacks; it owns NO state, NO
 * router, NO store. State (current page, fetched artifact) lives in the mount
 * container task that wraps this.
 *
 * Heartbeat/ECG SVG copied verbatim from the donor `qa-pastille.vue`.
 */
export interface PastilleProps {
  color: QaColor
  state: 'known' | 'no-tests' | 'unknown-page'
  strings: QaStrings
  onOpenModal: () => void
  onHover: () => void
}

export function Pastille({ color, state, strings, onOpenModal, onHover }: PastilleProps) {
  // The color → class mapping is the same for every state; only the title text
  // differs slightly so a hover hint can explain the no-tests / unknown-page cases.
  const base = strings['pastille.label']
  let detail = strings[QA_COLOR_LABEL_KEY[color]]
  if (state === 'no-tests') {
    detail = strings['panel.noTests']
  } else if (state === 'unknown-page') {
    detail = strings['pastille.unknownPage']
  }
  const label = `${base} — ${detail}`

  // Mirror the donor's two-element structure: `.qa-pastille` is the fixed-position
  // CONTAINER; `.qa-pastille__dot` carries the size/background/animation (the
  // `.qa-pastille__dot.qa--*` rules in styles.ts are where color resolves).
  return h(
    'div',
    { class: 'qa-pastille' },
    h(
      'button',
      {
        type: 'button',
        'data-testid': 'qa-pastille',
        class: `qa-pastille__dot ${QA_COLOR_MODIFIER[color]}`,
        'aria-label': label,
        title: label,
        onClick: onOpenModal,
        onMouseEnter: onHover,
        onFocus: onHover,
      },
      h(
        'svg',
        { class: 'qa-pastille__ecg', viewBox: '0 0 24 12', 'aria-hidden': 'true' },
        h('polyline', {
          points: '1,6 7,6 9,2 12,10 15,4 17,6 23,6',
          fill: 'none',
          stroke: 'currentColor',
          'stroke-width': '1.5',
          'stroke-linejoin': 'round',
          'stroke-linecap': 'round',
        }),
      ),
    ),
  )
}
